import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Check, Copy, Zap } from "lucide-react";

/**
 * Admin panel section: affiliate payout queue.
 *
 * Lists all affiliates with pending / requested commissions grouped by user,
 * so an admin can see the exact SEPA amount they need to transfer + copy
 * the IBAN into their banking app in one click.
 */
export default function AffiliatePayoutsAdmin() {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/affiliate/payouts");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (uid) => {
    if (!confirm(t("admAffMarkPaidConfirm"))) return;
    setBusy(uid);
    try {
      const r = await api.post(`/admin/affiliate/payouts/${uid}/mark-paid`);
      toast.success(t("admAffMarkedPaid", { n: r.data?.conversions ?? 0 }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admActionFailed"));
    } finally { setBusy(null); }
  };

  const payViaStripe = async (uid) => {
    if (!confirm(t("admAffPayStripeConfirm"))) return;
    setBusy(uid);
    try {
      const r = await api.post(`/admin/affiliate/payouts/${uid}/pay-via-stripe`, {});
      toast.success(t("admAffStripePaid", {
        n: r.data?.conversions ?? 0,
        amount: `€${((r.data?.amount_cents || 0) / 100).toFixed(2).replace(".", ",")}`,
      }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admActionFailed"));
    } finally { setBusy(null); }
  };

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success(t("admAffCopied")); } catch { /* noop */ }
  };

  return (
    <div data-testid="admin-affiliate-payouts">
      <p className="ed-eyebrow mb-2">{t("admAffTitle")}</p>
      <p className="text-sm font-serif italic text-neutral-600 mb-5 max-w-2xl">{t("admAffIntro")}</p>

      <div className="ed-card overflow-x-auto">
        <table className="ed-table">
          <thead>
            <tr>
              <th>{t("admAffColAffiliate")}</th>
              <th>{t("admAffColIban")}</th>
              <th className="text-right">{t("admAffColConversions")}</th>
              <th className="text-right">{t("admAffColAmount")}</th>
              <th>{t("admAffColStatus")}</th>
              <th className="text-right">{t("admAffColActions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={6} className="text-center py-8"><span className="ed-eyebrow">{t("loading")}</span></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 font-serif italic text-neutral-500" data-testid="admin-aff-empty">{t("admAffEmpty")}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.affiliate_user_id} data-testid={`admin-aff-row-${i}`}>
                <td>
                  <p className="font-serif text-base leading-tight">{r.full_name || "—"}</p>
                  <p className="font-mono text-[0.65rem] text-neutral-500">{r.email}</p>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-[0.7rem]">{r.iban}</code>
                    <button
                      data-testid={`admin-aff-copy-iban-${i}`}
                      onClick={() => copy(r.iban)}
                      className="p-1 hover:bg-neutral-100"
                      title={t("admAffCopyIban")}
                    >
                      <Copy size={11} strokeWidth={1.5} />
                    </button>
                  </div>
                  <p className="font-mono text-[0.6rem] text-neutral-500 mt-0.5">BIC: {r.bic || "—"}</p>
                </td>
                <td className="text-right font-mono text-xs">{r.conversions}</td>
                <td className="text-right font-serif text-lg" data-testid={`admin-aff-amount-${i}`}>
                  €{(r.cents / 100).toFixed(2).replace(".", ",")}
                </td>
                <td>
                  <span className={`ed-badge ${r.requested ? "badge-invited" : "badge-applied"}`}>
                    {t(r.requested ? "admAffStatusRequested" : "admAffStatusPending")}
                  </span>
                </td>
                <td>
                  <div className="flex justify-end gap-2 flex-wrap">
                    {r.stripe_ready && (
                      <button
                        data-testid={`admin-aff-stripe-pay-${i}`}
                        onClick={() => payViaStripe(r.affiliate_user_id)}
                        disabled={busy === r.affiliate_user_id}
                        className="px-2.5 py-1.5 border border-[#635BFF] text-white bg-[#635BFF] font-mono text-[0.6rem] uppercase tracking-wider hover:bg-[#4F47D6] transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <Zap size={11} strokeWidth={2} /> {t("admAffPayStripe")}
                      </button>
                    )}
                    <button
                      data-testid={`admin-aff-mark-paid-${i}`}
                      onClick={() => markPaid(r.affiliate_user_id)}
                      disabled={busy === r.affiliate_user_id}
                      className="px-2.5 py-1.5 border border-emerald-500 text-emerald-800 bg-emerald-50 font-mono text-[0.6rem] uppercase tracking-wider hover:bg-emerald-100 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      <Check size={11} strokeWidth={2} /> {t("admAffMarkPaid")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
